/**
 * CustomerActivityBlock — Iter 400.8 (rebuilt).
 *
 * Unified customer Timeline. Renders every `customer_activity` row —
 * fan-out from lead comments, JC comments, ticket comments, meeting
 * outcomes, trip debriefs, AND manual "Log Activity" entries (call,
 * whatsapp, email, site visit, note, voice note).
 *
 * Filter chips scope the view by kind so a rep can quickly see e.g.
 * only calls or only WhatsApp messages when handing over.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquareText, Sparkles, ExternalLink, User, Phone,
  MessageCircle, Mail, MapPin, StickyNote, Mic, Wrench,
  Ticket as TicketIcon, Target, Calendar as CalIcon, Plus,
} from "lucide-react";
import FoldBlock from "./FoldBlock";

const KIND_META = {
  call:            { icon: Phone,         label: "Call",         color: "#22d3ee" },
  whatsapp:        { icon: MessageCircle, label: "WhatsApp",     color: "#10b981" },
  email:           { icon: Mail,          label: "Email",        color: "#a78bfa" },
  site_visit:      { icon: MapPin,        label: "Site visit",   color: "#f59e0b" },
  note:            { icon: StickyNote,    label: "Note",         color: "#94a3b8" },
  voice_note:      { icon: Mic,           label: "Voice note",   color: "#f43f5e" },
  lead_comment:    { icon: Target,        label: "Lead comment", color: "#22d3ee" },
  jobcard_comment: { icon: Wrench,        label: "Job card",     color: "#f59e0b" },
  ticket_comment:  { icon: TicketIcon,    label: "Ticket",       color: "#a78bfa" },
  meeting_outcome: { icon: CalIcon,       label: "Meeting",      color: "#22d3ee" },
  meeting_comment: { icon: MessageSquareText, label: "Meeting", color: "#a78bfa" },
  trip_debrief:    { icon: Sparkles,      label: "Trip debrief", color: "#f59e0b" },
};

function metaFor(k) {
  return KIND_META[k] || { icon: MessageSquareText, label: (k || "activity").replace(/_/g, " "), color: "#94a3b8" };
}

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso.slice(0, 16).replace("T", " "); }
}

export default function CustomerActivityBlock({ events, onLogClick }) {
  const items = Array.isArray(events) ? events : [];
  const [filter, setFilter] = useState("all");

  const kinds = useMemo(() => {
    const s = new Set(items.map((e) => e.kind).filter(Boolean));
    return Array.from(s);
  }, [items]);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((e) => e.kind === filter);
  }, [items, filter]);

  return (
    <FoldBlock
      icon={MessageSquareText}
      iconColor="#A78BFA"
      title="Timeline"
      count={items.length}
      defaultOpen={items.length > 0}
      testId="block-customer-activity"
      alwaysRenderChildren
      emptyHint="No touchpoints logged yet. Use the +Log button above to log a call, WhatsApp, email, note or voice memo."
    >
      <div className="px-3 py-2 space-y-2">
        {/* Filter chips + Log Activity CTA */}
        <div className="flex items-center gap-1 flex-wrap">
          <button
            type="button"
            onClick={() => setFilter("all")}
            className={`px-2 py-0.5 rounded text-[10px] font-medium ${
              filter === "all" ? "bg-[#22d3ee]/20 text-[#22d3ee]" : "text-[#a0aabf] hover:text-white bg-white/[0.03]"
            }`}
            data-testid="activity-filter-all"
          >
            All ({items.length})
          </button>
          {kinds.map((k) => {
            const m = metaFor(k);
            const KIcon = m.icon;
            const count = items.filter((e) => e.kind === k).length;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setFilter(k)}
                className={`px-2 py-0.5 rounded text-[10px] font-medium inline-flex items-center gap-1 ${
                  filter === k ? "bg-[#22d3ee]/20 text-[#22d3ee]" : "text-[#a0aabf] hover:text-white bg-white/[0.03]"
                }`}
                style={filter !== k ? { color: m.color } : undefined}
                data-testid={`activity-filter-${k}`}
              >
                <KIcon className="w-3 h-3" /> {m.label} · {count}
              </button>
            );
          })}
          {onLogClick && (
            <button
              type="button"
              onClick={onLogClick}
              className="ml-auto hv-btn hv-btn-primary text-[10px] inline-flex items-center gap-1"
              data-testid="activity-log-btn"
            >
              <Plus className="w-3 h-3" /> Log activity
            </button>
          )}
        </div>

        {/* Feed */}
        {filtered.length === 0 ? (
          <div className="text-center text-xs text-[#647087] py-4" data-testid="activity-empty">
            Nothing here for this filter.
          </div>
        ) : (
          <div className="space-y-2" data-testid="customer-activity-list">
            {filtered.map((ev) => {
              const m = metaFor(ev.kind);
              const Icon = m.icon;
              return (
                <div
                  key={ev.id || ev.created_at}
                  className="hv-card border border-white/5 p-3 space-y-1"
                  data-testid={`customer-activity-row-${ev.id}`}
                >
                  <div className="flex items-center justify-between gap-2 text-[11px] text-[#94a3b8]">
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon className="w-3.5 h-3.5" style={{ color: m.color }} />
                      <span className="font-semibold tracking-wide uppercase text-[10px]" style={{ color: m.color }}>
                        {m.label}
                      </span>
                      {ev.by_user_name && (
                        <span className="flex items-center gap-1 text-[#647087] truncate">
                          <User className="w-3 h-3" /> {ev.by_user_name}
                        </span>
                      )}
                      {ev.counterparty && (
                        <span className="text-[#647087] text-[10px]">
                          → {ev.counterparty}
                        </span>
                      )}
                      {ev.duration_minutes != null && (
                        <span className="text-[10px] font-mono text-[#647087]">
                          {ev.duration_minutes}m
                        </span>
                      )}
                    </div>
                    <time className="font-mono text-[10px] shrink-0">{fmt(ev.created_at)}</time>
                  </div>
                  {ev.summary && (
                    <p className="text-sm text-[#e5e7eb] whitespace-pre-wrap leading-relaxed">
                      {ev.summary}
                    </p>
                  )}
                  {ev.outcome && (
                    <p className="text-xs text-[#cbd5e1]">
                      <span className="text-[#647087] font-medium">Outcome:</span> {ev.outcome}
                    </p>
                  )}
                  {ev.next_step && (
                    <p className="text-xs text-[#cbd5e1]">
                      <span className="text-[#647087] font-medium">Next step:</span> {ev.next_step}
                    </p>
                  )}
                  {ev.jobcard_id && (
                    <Link
                      to={`/jobcards/${ev.jobcard_id}`}
                      className="inline-flex items-center gap-1 text-[11px] text-[#f59e0b] hover:underline"
                    >
                      Open job card {ev.jobcard_short_id ? `#${ev.jobcard_short_id}` : ""} <ExternalLink className="w-3 h-3" />
                    </Link>
                  )}
                  {ev.ticket_id && (
                    <Link
                      to={`/tickets/${ev.ticket_id}`}
                      className="inline-flex items-center gap-1 text-[11px] text-[#a78bfa] hover:underline"
                    >
                      Open ticket {ev.ticket_short_id ? `#${ev.ticket_short_id}` : ""} <ExternalLink className="w-3 h-3" />
                    </Link>
                  )}
                  {ev.trip_id && (
                    <Link
                      to={`/trips/${ev.trip_id}`}
                      className="inline-flex items-center gap-1 text-[11px] text-[#22d3ee] hover:underline"
                    >
                      Open trip <ExternalLink className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </FoldBlock>
  );
}
